import React, { useEffect, useState } from 'react';
import PageExplainer from '@/components/PageExplainer';
import Navigation from '@/components/Navigation';
import MemberAvatar from '@/components/MemberAvatar';
import AvailabilityEditor from '@/components/AvailabilityEditor';
import MyEventSheet from '@/components/MyEventSheet';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Calendar, MapPin, Users, Tag, Ticket, CreditCard, LogIn, Clock, Car, Info, Award, User, ExternalLink, ChevronRight, Eye, EyeOff, Search, Lock, MessageSquare, CalendarPlus, CalendarCheck } from 'lucide-react';
import usePageMeta from '@/hooks/usePageMeta';
import { SponsorBanner } from '@/components/SponsorBanner';
import RichText from '@/components/RichText';
import PageSideRail from '@/components/PageSideRail';
import MemberProfileModal from '@/components/MemberProfileModal';
import { toast } from 'sonner';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Linkify from '@/lib/linkify';

const BACKEND_URL = '';
const MAX_VISIBLE = typeof window !== 'undefined' && window.innerWidth < 640 ? 5 : 8;

// Search normaliser — case-insensitive, and `&` / `and` are treated as the
// same character so `Marks & Spencer` matches when the user types
// `marks and spencer` (and vice versa). Trailing/leading whitespace is
// collapsed so a stray space doesn't kill an otherwise-good match.
function normaliseForSearch(str) {
  return String(str || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/\s+/g, ' ')
    .trim();
}

function AttendeeAvatar({ attendee, size = 'sm' }) {
  // Delegates to the shared MemberAvatar — gradient + black-initial
  // fallback fires correctly when the avatar endpoint 404s for users
  // without a real photo. Title-tooltip preserved via wrapping span.
  // Sizes:
  //   sm — 32px (used in the inline "+6" attendee bubble pill)
  //   md — 48px (used in the All Attendees modal rows)
  const px = size === 'sm' ? 32 : 48;
  return (
    <span title={attendee.name}>
      <MemberAvatar
        name={attendee.name}
        picture={attendee.picture}
        size={px}
        ringClassName={size === 'sm' ? 'ring-2 ring-white' : ''}
      />
    </span>
  );
}

// Inline payment panel — sits above the Pay button on a paid event card
// when the member is registered but payment is still pending. Handles the
// optional promo-code entry, live re-price, and rejection messaging.
function PayPanel({ event, priceLabel, onPay, headers }) {
  const [codeInput, setCodeInput] = React.useState('');
  const [appliedCode, setAppliedCode] = React.useState(null); // { code, final_amount, discount_amount, free }
  const [rejection, setRejection] = React.useState('');
  const [validating, setValidating] = React.useState(false);

  const ccy = (event.ticket_currency || 'GBP').toUpperCase();

  const applyCode = async () => {
    const trimmed = codeInput.trim();
    if (!trimmed) return;
    setValidating(true);
    setRejection('');
    try {
      const res = await fetch(`/api/promo-codes/validate`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: trimmed, event_id: event.event_id }),
      });
      const data = await res.json();
      if (!res.ok) {
        setAppliedCode(null);
        setRejection(data.detail || "This code isn't valid for this event.");
      } else {
        setAppliedCode(data);
        setRejection('');
      }
    } catch {
      setAppliedCode(null);
      setRejection("Couldn't check that code — try again.");
    }
    setValidating(false);
  };

  const clearCode = () => {
    setAppliedCode(null);
    setRejection('');
    setCodeInput('');
  };

  const displayedTotal = appliedCode
    ? (appliedCode.free
        ? 'FREE'
        : `${ccy} ${Number(appliedCode.final_amount).toFixed(2)}`)
    : priceLabel;

  const btnLabel = appliedCode?.free ? 'Claim free ticket' : `Pay ${displayedTotal}`;
  const btnClass = appliedCode?.free
    ? 'w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white'
    : 'w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white';

  return (
    <div className="flex-1 flex flex-col gap-2" data-testid={`pay-panel-${event.event_id}`}>
      <div className="flex items-center justify-between text-xs px-2 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-800">
        <span><Ticket className="h-3.5 w-3.5 inline mr-1" /> Registered · payment pending</span>
      </div>

      {/* Promo code row */}
      {appliedCode ? (
        <div className="flex items-center justify-between px-2 py-1.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs" data-testid={`pay-panel-applied-${event.event_id}`}>
          <span className="truncate">
            Code <strong className="font-mono">{appliedCode.code}</strong> applied
            {!appliedCode.free && (
              <> — save {ccy} {Number(appliedCode.discount_amount).toFixed(2)}</>
            )}
          </span>
          <button
            onClick={clearCode}
            className="text-emerald-700 hover:text-emerald-900 underline ml-2 flex-shrink-0"
            data-testid={`pay-panel-clear-code-${event.event_id}`}
          >Remove</button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Input
            data-testid={`pay-panel-code-input-${event.event_id}`}
            value={codeInput}
            onChange={e => { setCodeInput(e.target.value.toUpperCase()); if (rejection) setRejection(''); }}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); applyCode(); } }}
            placeholder="Have a promo code?"
            className="h-9 text-sm uppercase font-mono"
            aria-label="Promo code"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={applyCode}
            disabled={!codeInput.trim() || validating}
            data-testid={`pay-panel-apply-code-${event.event_id}`}
            className="h-9"
          >
            {validating ? '…' : 'Apply'}
          </Button>
        </div>
      )}
      {rejection && (
        <p className="text-xs text-red-600" data-testid={`pay-panel-rejection-${event.event_id}`}>{rejection}</p>
      )}

      <Button
        data-testid={`pay-button-${event.event_id}`}
        onClick={() => onPay(event.event_id, appliedCode?.code)}
        className={btnClass}
      >
        <CreditCard className="h-4 w-4 mr-2" /> {btnLabel}
      </Button>
    </div>
  );
}

export default function EventsPage({ user: userProp }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAttendees, setShowAttendees] = useState(null);
  const [viewingMember, setViewingMember] = useState(null);
  const [meetRecs, setMeetRecs] = useState([]);
  const [directorySearch, setDirectorySearch] = useState('');
  const [adminShowHidden, setAdminShowHidden] = useState(false);
  const [showAvailabilityFor, setShowAvailabilityFor] = useState(null);
  const [showMyEventFor, setShowMyEventFor] = useState(null);
  // Per-event meeting-request map, keyed by event_id. Populated lazily when
  // a card renders — we only hit the backend for events the member is
  // registered on, so the extra traffic stays bounded.
  const [meetingsByEvent, setMeetingsByEvent] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(userProp || null);
  const navigate = useNavigate();

  // Self-manage auth state since this page is public (not wrapped in ProtectedRoute).
  // We do the SAME hydration steps ProtectedRoute does:
  //   1. Use userProp if it was passed
  //   2. Use cached `sll_user` if present
  //   3. Otherwise probe /api/auth/session — the user may have a valid
  //      session cookie even though localStorage was never populated on
  //      this device/tab (e.g. they hit /events as their first action).
  // Without step 3 the page rendered as anonymous for authenticated users
  // and the nav showed the "?" icon + "Sign In / Register" CTA.
  useEffect(() => {
    if (userProp) { setUser(userProp); return; }
    const cached = sessionStorage.getItem('sll_user') || localStorage.getItem('sll_user');
    const cookieToken = sessionStorage.getItem('session_token') || localStorage.getItem('sll_session_token');
    if (cached) {
      try { setUser(JSON.parse(cached)); } catch {}
    }
    // Always re-verify the cookie in the background — corrects any stale
    // cached payload and hydrates from cookie if no cache exists.
    fetch(`${BACKEND_URL}/api/auth/session`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(freshUser => {
        if (freshUser) {
          setUser(freshUser);
          const s = JSON.stringify(freshUser);
          sessionStorage.setItem('sll_user', s);
          localStorage.setItem('sll_user', s);
        }
      })
      .catch(() => {});
    // Mark the void to silence eslint about an unused var when only used
    // as a hint for the comment above.
    void cookieToken;
  }, [userProp]);

  const isLoggedIn = !!user;
  const token = sessionStorage.getItem('session_token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // Per-event browser title / share meta when an attendee list is open.
  usePageMeta({ title: 'Events' });

  const loadEvents = () => {
    fetch(`${BACKEND_URL}/api/events`, { credentials: 'include', headers })
      .then(res => res.json())
      .then(data => {
        setEvents(data);
        setLoading(false);
        // Preload meeting-request lists for events the member is registered
        // on so the "My meetings" strip paints without a per-card delay.
        (data || []).forEach(ev => {
          if (ev.my_registration) loadMeetingsForEvent(ev.event_id);
        });
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadEvents(); }, []);

  useEffect(() => {
    if (!isLoggedIn) return;
    const payment = searchParams.get('payment');
    const eventId = searchParams.get('event_id');
    const ticketId = searchParams.get('ticket_id');
    const sessionId = searchParams.get('session_id');
    if (payment === 'success' && eventId) {
      fetch(`${BACKEND_URL}/api/events/${eventId}/confirm-payment`, {
        method: 'POST', credentials: 'include', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket_id: ticketId, session_id: sessionId }),
      }).then(r => r.json()).then(() => {
        toast.success('Payment confirmed! Your badge has been sent to your email.');
        loadEvents();
      }).catch(() => toast.error('Payment recorded but badge email may have failed.'));
      setSearchParams({});
    } else if (payment === 'cancelled') {
      toast.error('Payment was cancelled.');
      setSearchParams({});
    }
  }, []);

  const handleRSVP = async (eventId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/events/${eventId}/rsvp`, { method: 'POST', credentials: 'include', headers });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'RSVP failed');
      if (data.requires_payment) {
        toast.success(`Registered! Click "Pay ${(data.ticket_currency || 'GBP').toUpperCase()} ${Number(data.ticket_price).toFixed(2)}" to confirm your ticket.`);
      } else {
        toast.success(data.ticket_id ? `Registered! Badge sent to your email. Ticket: ${data.ticket_id}` : 'RSVP confirmed!');
      }
      loadEvents();
    } catch (e) { toast.error(e.message || 'Failed to RSVP.'); }
  };

  const handlePay = async (eventId, promoCode) => {
    try {
      toast.info('Redirecting to secure payment…');
      const checkoutRes = await fetch(`${BACKEND_URL}/api/events/${eventId}/checkout`, {
        method: 'POST', credentials: 'include',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(promoCode ? { promo_code: promoCode } : {}),
      });
      const checkout = await checkoutRes.json();
      if (!checkoutRes.ok) throw new Error(checkout.detail || 'Could not create checkout session');
      // Zero-total short-circuit — ticket already paid, no Stripe redirect.
      if (checkout.free) {
        toast.success('Ticket claimed! Badge sent to your email.');
        loadEvents();
        return;
      }
      if (checkout.checkout_url) { window.location.href = checkout.checkout_url; return; }
      throw new Error('No checkout URL returned');
    } catch (e) { toast.error(e.message || 'Payment could not be started.'); }
  };

  // Load meet matches (for "Match" badges) whenever the user opens the attendees list for an event they're registered for.
  useEffect(() => {
    const ev = showAttendees;
    if (!ev || !isLoggedIn) { setMeetRecs([]); return; }
    if (!ev.my_registration) { setMeetRecs([]); return; }
    fetch(`${BACKEND_URL}/api/events/${ev.event_id}/meet-recommendations`, {
      credentials: 'include', headers,
    })
      .then(r => r.ok ? r.json() : { recommendations: [] })
      .then(d => setMeetRecs(d.recommendations || []))
      .catch(() => setMeetRecs([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAttendees?.event_id, isLoggedIn]);

  // Reset directory-panel filters/toggles whenever the modal changes event
  useEffect(() => {
    setDirectorySearch('');
    setAdminShowHidden(false);
  }, [showAttendees?.event_id]);

  // Load every meeting request involving the caller for a given event.
  // Called lazily on cards where the user is registered so we don't fan
  // out unnecessarily on the public events list.
  const loadMeetingsForEvent = async (eventId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/events/${eventId}/meeting-requests`,
        { credentials: 'include', headers });
      if (res.ok) {
        const rows = await res.json();
        setMeetingsByEvent(prev => ({ ...prev, [eventId]: rows }));
      }
    } catch { /* non-fatal — card just hides the strip */ }
  };

  // Mutate a meeting request (accept / decline / cancel). Shared by the
  // event-card inbound invite tiles AND the outbound-pending row.
  const handleMeetingAction = async (requestId, status, eventId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/meeting-requests/${requestId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not update meeting');
      toast.success(
        status === 'accepted' ? 'Meeting accepted — on your schedule.' :
        status === 'declined' ? 'Request declined.' :
        'Meeting cancelled.'
      );
      await loadMeetingsForEvent(eventId);
    } catch (e) { toast.error(e.message); }
  };

  // Attendee-directory visibility toggle. Optimistically updates the local
  // event so both the header chip and the small avatar strip re-render
  // without waiting for a full events refetch.
  const handleToggleVisibility = async (eventId, hidden) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/events/${eventId}/visibility`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ hidden }),
      });
      if (!res.ok) throw new Error();
      // Optimistic patch — keep the panel + card in sync without a refetch
      setEvents(prev => prev.map(ev => {
        if (ev.event_id !== eventId) return ev;
        const next = { ...ev };
        next.my_registration = {
          ...(ev.my_registration || {}),
          visibility_hidden: hidden,
        };
        // Update the caller's own row inside attendees + attendee_count
        const uid = user?.user_id;
        const others = (ev.attendees || []).filter(a => a.user_id !== uid);
        const self = (ev.attendees || []).find(a => a.user_id === uid);
        if (hidden) {
          // Keep self entry with the flag so the panel still shows "you're hidden"
          next.attendees = self ? [...others, { ...self, visibility_hidden: true }] : others;
          next.attendee_count = others.length;
        } else if (self) {
          const { visibility_hidden: _drop, ...rest } = self;
          next.attendees = [...others, rest];
          next.attendee_count = next.attendees.length;
        }
        return next;
      }));
      if (showAttendees?.event_id === eventId) {
        setShowAttendees(prev => {
          if (!prev) return prev;
          const uid = user?.user_id;
          const others = (prev.attendees || []).filter(a => a.user_id !== uid);
          const self = (prev.attendees || []).find(a => a.user_id === uid);
          const nextAttendees = hidden
            ? (self ? [...others, { ...self, visibility_hidden: true }] : others)
            : (self ? [...others, (({ visibility_hidden: _drop, ...rest }) => rest)(self)] : others);
          return {
            ...prev,
            my_registration: { ...(prev.my_registration || {}), visibility_hidden: hidden },
            attendees: nextAttendees,
            attendee_count: nextAttendees.length,
          };
        });
      }
      toast.success(hidden ? "You're now hidden from other attendees." : "You're visible to other attendees again.");
    } catch { toast.error('Could not update visibility — try again.'); }
  };

  const hasDetails = (ev) => (ev.agenda?.length > 0 || ev.speakers_detail?.length > 0 || ev.parking_info || ev.additional_info || ev.event_sponsors?.length > 0);

  const formatDate = (d) => {
    try { return new Date(d).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }); }
    catch { return d; }
  };

  // Normalise stored format strings ("Inperson", "in-person", "IN_PERSON")
  // to a single human-readable label. Older events were saved with
  // inconsistent casing/punctuation.
  const formatEventFormat = (f) => {
    if (!f) return '';
    const norm = String(f).toLowerCase().replace(/[-_\s]/g, '');
    if (norm === 'inperson') return 'In person';
    if (norm === 'online') return 'Online';
    if (norm === 'hybrid') return 'Hybrid';
    return f.charAt(0).toUpperCase() + f.slice(1);
  };

  const RSVPButton = ({ event }) => {
    if (!isLoggedIn) {
      return (
        <Button data-testid={`signin-rsvp-button-${event.event_id}`} onClick={() => navigate('/login')} className="flex-1 bg-gradient-to-r from-brand-navy to-brand-sage hover:from-brand-dark hover:to-brand-sage text-white">
          <LogIn className="h-4 w-4 mr-2" /> Sign In to RSVP
        </Button>
      );
    }

    const isRegistered = event.rsvps?.includes(user?.user_id);
    const myReg = event.my_registration;
    const paymentStatus = myReg?.payment_status;
    const isPaid = event.ticket_price > 0;
    const ccy = (event.ticket_currency || 'GBP').toUpperCase();
    const priceLabel = isPaid ? `${ccy} ${Number(event.ticket_price).toFixed(2)}` : '';

    // Already registered + paid (or free event)
    if (isRegistered && (paymentStatus === 'paid' || paymentStatus === 'free' || !isPaid)) {
      return (
        <Button data-testid={`rsvp-button-${event.event_id}`} disabled
          className="flex-1 bg-gradient-to-r from-brand-navy to-brand-sage text-white">
          <Ticket className="h-4 w-4 mr-2" /> Registered{isPaid ? ' · Paid' : ''}
        </Button>
      );
    }

    // Registered for paid event, payment still pending → show payment panel
    // with an optional promo-code input above the pay button.
    if (isRegistered && isPaid) {
      return <PayPanel event={event} priceLabel={priceLabel} onPay={handlePay} headers={headers} />;
    }

    // Sold out
    if (event.spots_remaining === 0) {
      return (
        <Button disabled className="flex-1 bg-slate-300 text-white" data-testid={`rsvp-button-${event.event_id}`}>Sold Out</Button>
      );
    }

    // Not yet registered
    return (
      <Button data-testid={`rsvp-button-${event.event_id}`} onClick={() => handleRSVP(event.event_id)}
        className="flex-1 bg-gradient-to-r from-brand-navy to-brand-sage hover:from-brand-dark hover:to-brand-sage text-white">
        {isPaid ? <><Ticket className="h-4 w-4 mr-2" /> Register · {priceLabel}</> : 'RSVP Now'}
      </Button>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <main className="pt-24 pb-12 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          <SponsorBanner placement="enablers-events" type="top" className="mb-4 md:mb-6 flex justify-center" />
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 min-w-0">
          <div className="mb-4 md:mb-8">
            <h1 className="text-2xl md:text-4xl font-bold font-heading mb-1 md:mb-2 gradient-text flex items-center gap-2">Events <PageExplainer pageKey="events" label="About Events" /></h1>
            <p className="text-sm md:text-base text-slate-600">Join us at our inclusive SLL events throughout the year</p>
          </div>

          {!isLoggedIn && (
            <div data-testid="guest-cta-banner" className="mb-8 p-6 rounded-xl bg-gradient-to-r from-brand-navy/5 to-brand-sage/10 border border-brand-sage/20">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-brand-navy mb-1">Want to attend these events?</h3>
                  <p className="text-sm text-slate-600">Join the SLL community to RSVP, network with attendees, and access exclusive content.</p>
                </div>
                <div className="flex gap-3 flex-shrink-0">
                  <Button data-testid="guest-signin-btn" variant="outline" onClick={() => navigate('/login')} className="border-brand-navy text-brand-navy hover:bg-brand-navy/5">Sign In</Button>
                  <Button data-testid="guest-register-btn" onClick={() => navigate('/login')} className="bg-brand-navy hover:bg-brand-dark text-white">Register Now</Button>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-16"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" /></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {events.map(event => {
                // Small avatar strip on the card honours visibility for all
                // viewers (including admins) — hidden attendees only appear
                // inside the directory panel behind the admin "Show hidden"
                // toggle. Self-when-hidden is stripped so the member's own
                // avatar disappears from the strip they share with peers.
                const attendees = (event.attendees || []).filter(a => !a.visibility_hidden);
                const visible = attendees.slice(0, MAX_VISIBLE);
                const overflow = attendees.length - MAX_VISIBLE;

                return (
                  <Card key={event.event_id} data-testid={`event-card-${event.event_id}`} className="min-w-0 p-5 md:p-8 border border-slate-100 shadow-xl shadow-slate-200/50 hover:shadow-2xl transition-all overflow-hidden">
                    <div className="mb-4 md:mb-6">
                      <div className="flex items-start justify-between gap-3">
                        <h2 className="text-lg md:text-2xl font-bold font-heading mb-2">{event.title}</h2>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          {event.ticket_price > 0 ? (
                            <span className="px-2 md:px-3 py-1 bg-amber-50 text-amber-700 text-xs md:text-sm font-bold rounded-full border border-amber-200" data-testid={`event-price-${event.event_id}`}>
                              {(event.ticket_currency || 'GBP').toUpperCase()} {event.ticket_price.toFixed(2)}
                            </span>
                          ) : (
                            <span className="px-2 md:px-3 py-1 bg-green-50 text-green-700 text-xs md:text-sm font-semibold rounded-full border border-green-200">Free</span>
                          )}
                          {event.spots_remaining !== null && event.spots_remaining !== undefined && (
                            <span className={`text-xs font-medium ${event.spots_remaining === 0 ? 'text-red-500' : event.spots_remaining <= 5 ? 'text-amber-600' : 'text-slate-500'}`} data-testid={`event-spots-${event.event_id}`}>
                              {event.spots_remaining === 0 ? 'SOLD OUT' : `${event.spots_remaining} spot${event.spots_remaining !== 1 ? 's' : ''} left`}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-sm md:text-base text-slate-600 leading-relaxed">
                        <RichText html={event.description} className="card card-3" />
                      </div>
                    </div>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-center text-slate-600">
                        <Calendar className="h-5 w-5 mr-3 text-brand-navy" />
                        <span>
                          {formatDate(event.date)}
                          {event.start_time && (
                            <span className="ml-2 text-slate-500">
                              &middot; {event.start_time}{event.end_time ? ` – ${event.end_time}` : ''}
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center text-slate-600">
                        <MapPin className="h-5 w-5 mr-3 text-brand-navy" />
                        <span>{event.location} {event.format && `\u00b7 ${formatEventFormat(event.format)}`}</span>
                      </div>

                      {/* Speakers preview */}
                      {event.speakers_detail?.length > 0 && (
                        <div className="flex items-start gap-2 text-slate-600 min-w-0">
                          <User className="h-5 w-5 text-brand-navy flex-shrink-0 mt-0.5" />
                          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 min-w-0">
                            {event.speakers_detail.slice(0, 3).map((s, i) => (
                              <div key={i} className="flex items-center gap-1.5">
                                <div className="w-6 h-6 rounded-full bg-slate-100 overflow-hidden flex-shrink-0">
                                  {s.headshot_url ? <img src={s.headshot_url} alt="" className="w-full h-full object-cover" /> : <User className="w-full h-full p-1 text-slate-300" />}
                                </div>
                                <span className="text-sm whitespace-nowrap">{s.name.split(',')[0]}</span>
                                {i < Math.min(event.speakers_detail.length, 3) - 1 && <span className="text-slate-300">&middot;</span>}
                              </div>
                            ))}
                            {event.speakers_detail.length > 3 && <span className="text-xs text-slate-400">+{event.speakers_detail.length - 3} more</span>}
                          </div>
                        </div>
                      )}

                      {event.topic_tags?.length > 0 && (
                        <div className="flex items-center flex-wrap gap-2">
                          <Tag className="h-5 w-5 text-brand-navy" />
                          {event.topic_tags.map((tag, idx) => (
                            <span key={idx} className="px-3 py-1 bg-brand-navy/5 text-brand-navy text-sm rounded-full">{tag}</span>
                          ))}
                        </div>
                      )}

                      {(event.attendee_count > 0 || attendees.length > 0) && (
                        <div className="flex items-start gap-3 pt-1 min-w-0" data-testid={`event-attendees-${event.event_id}`}>
                          <Users className="h-5 w-5 text-brand-navy flex-shrink-0 mt-1.5" />
                          <div className="min-w-0 flex-1 space-y-1">
                          <div className="flex items-center -space-x-2 py-0.5 pl-0.5">
                            {visible.map(a => <AttendeeAvatar key={a.user_id} attendee={a} />)}
                            {overflow > 0 && (
                              <button data-testid={`attendees-overflow-${event.event_id}`} onClick={() => setShowAttendees(event)}
                                className="w-8 h-8 rounded-full ring-2 ring-white bg-slate-200 hover:bg-slate-300 flex items-center justify-center text-xs font-bold text-slate-600 transition-colors flex-shrink-0">
                                +{overflow}
                              </button>
                            )}
                          </div>
                          <button onClick={() => setShowAttendees(event)} className="text-sm text-slate-500 hover:text-brand-navy transition-colors text-left" data-testid={`attendees-count-${event.event_id}`}>
                            {event.attendee_count ?? attendees.length} BriteHub Member{(event.attendee_count ?? attendees.length) !== 1 ? 's' : ''} Registered to Attend
                          </button>
                          </div>
                        </div>
                      )}

                      {/* Visibility toggle + unread-messages chip — both
                          only appear for members with a registration on
                          this event. The unread chip deep-links straight
                          into the event-filtered inbox thread. */}
                      {isLoggedIn && event.my_registration && (
                        <>
                          {/* Mobile-prominent "My Event" entry — the single
                              button that opens the attendee's personal
                              bottom-sheet view of this day with Attendees
                              / Messages / Availability + chronological
                              schedule including accepted meetings. */}
                          <Button
                            type="button"
                            onClick={() => setShowMyEventFor(event)}
                            data-testid={`event-open-my-event-${event.event_id}`}
                            className="w-full sm:w-auto self-start bg-gradient-to-r from-brand-navy to-brand-sage hover:from-brand-dark hover:to-brand-sage text-white"
                            size="sm"
                          >
                            <CalendarCheck className="h-4 w-4 mr-1.5" /> View your scheduled sessions here
                            {(event.my_registration.unread_event_messages || 0) > 0 && (
                              <span className="ml-1.5 inline-flex items-center justify-center text-[10px] font-bold bg-white/25 rounded-full px-1.5 min-w-[18px] h-[18px]">
                                {event.my_registration.unread_event_messages}
                              </span>
                            )}
                          </Button>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 self-start">
                          <button
                            type="button"
                            onClick={() => handleToggleVisibility(event.event_id, !event.my_registration.visibility_hidden)}
                            data-testid={`event-visibility-toggle-${event.event_id}`}
                            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-brand-navy transition-colors"
                            title={event.my_registration.visibility_hidden
                              ? 'Click to become visible in the attendee directory'
                              : 'Click to hide from the attendee directory'}
                          >
                            {event.my_registration.visibility_hidden ? (
                              <><EyeOff className="h-3.5 w-3.5" /> Hidden from attendees</>
                            ) : (
                              <><Eye className="h-3.5 w-3.5" /> Visible to attendees</>
                            )}
                          </button>
                          {(event.my_registration.unread_event_messages || 0) > 0 && (
                            <button
                              type="button"
                              onClick={() => navigate(`/messages?event_id=${event.event_id}`)}
                              data-testid={`event-unread-messages-${event.event_id}`}
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-brand-navy hover:bg-brand-dark rounded-full px-2.5 py-0.5 transition-colors"
                              title="Open event-tagged messages"
                            >
                              <MessageSquare className="h-3 w-3" /> {event.my_registration.unread_event_messages} new
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setShowAvailabilityFor(event)}
                            data-testid={`event-set-availability-${event.event_id}`}
                            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-brand-navy transition-colors"
                            title="Set when you're free to meet"
                          >
                            <CalendarPlus className="h-3.5 w-3.5" /> Set availability
                          </button>
                        </div>
                        </>
                      )}

                      {/* My meetings strip — only renders for registered
                          members with at least one live meeting on this
                          event. Pending-inbound shows Accept/Decline;
                          outbound-pending shows a plain "Cancel"; accepted
                          shows the agreed time + "Cancel". */}
                      {isLoggedIn && event.my_registration && (meetingsByEvent[event.event_id] || []).some(m => m.status === 'pending' || m.status === 'accepted') && (
                        <div className="rounded-lg border border-brand-sage/30 bg-brand-sage/5 p-2.5 space-y-1.5" data-testid={`event-meetings-strip-${event.event_id}`}>
                          <p className="text-[10px] font-semibold text-brand-sage uppercase tracking-wider flex items-center gap-1.5">
                            <CalendarPlus className="h-3 w-3" /> My meetings
                          </p>
                          {(meetingsByEvent[event.event_id] || [])
                            .filter(m => m.status === 'pending' || m.status === 'accepted')
                            .map(m => {
                              const isInbound = m.recipient_id === user?.user_id && m.status === 'pending';
                              const isOutbound = m.requester_id === user?.user_id && m.status === 'pending';
                              const partner = m.requester_id === user?.user_id ? m.recipient_name : m.requester_name;
                              return (
                                <div key={m.request_id} className="flex items-center gap-2 text-xs flex-wrap" data-testid={`event-meeting-${m.request_id}`}>
                                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                                    m.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                                  }`} data-testid={`event-meeting-status-${m.request_id}`}>
                                    {m.status}
                                  </span>
                                  <span className="flex-1 min-w-0 truncate text-slate-700">
                                    <strong className="font-semibold">{partner}</strong>
                                    <span className="text-slate-500"> · {m.start_time}–{m.end_time}</span>
                                    {m.meeting_point && <span className="text-slate-400"> · {m.meeting_point}</span>}
                                  </span>
                                  {isInbound && (
                                    <>
                                      <button type="button" onClick={() => handleMeetingAction(m.request_id, 'accepted', event.event_id)}
                                        className="text-emerald-700 hover:text-emerald-900 font-semibold"
                                        data-testid={`event-meeting-accept-${m.request_id}`}>Accept</button>
                                      <button type="button" onClick={() => handleMeetingAction(m.request_id, 'declined', event.event_id)}
                                        className="text-slate-500 hover:text-slate-700"
                                        data-testid={`event-meeting-decline-${m.request_id}`}>Decline</button>
                                    </>
                                  )}
                                  {(isOutbound || m.status === 'accepted') && (
                                    <button type="button" onClick={() => handleMeetingAction(m.request_id, 'cancelled', event.event_id)}
                                      className="text-slate-400 hover:text-rose-600"
                                      data-testid={`event-meeting-cancel-${m.request_id}`}>Cancel</button>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <RSVPButton event={event} />
                      {hasDetails(event) && (
                        <Button variant="outline" onClick={() => {
                          // Same intent ping as courses — log the click,
                          // skip if the user is already registered.
                          const myReg = event.my_registration;
                          if (!myReg || myReg.payment_status === 'pending') {
                            try {
                              fetch(`${BACKEND_URL}/api/booking-intents`, {
                                method: 'POST', credentials: 'include',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ target_type: 'event', target_id: event.event_id }),
                                keepalive: true,
                              }).catch(() => {});
                            } catch { /* ignore */ }
                          }
                          navigate(`/events/${event.event_id}`);
                        }} data-testid={`view-details-${event.event_id}`} className="w-full sm:w-auto h-auto min-h-9 py-2 whitespace-normal text-brand-navy border-brand-navy/20 hover:bg-brand-navy/5">
                          <ChevronRight className="h-4 w-4 mr-1" /> View the agenda and book BriteLabs
                        </Button>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          {!loading && events.length === 0 && (
            <div className="text-center py-16"><p className="text-slate-500">No events scheduled at the moment</p></div>
          )}
            </div>
            <PageSideRail placement="enablers-events" />
          </div>
        </div>
      </main>

      {/* Attendee Directory */}
      <Dialog open={!!showAttendees} onOpenChange={() => setShowAttendees(null)}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[85vh] overflow-y-auto p-4 md:p-6" data-testid="attendees-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-base md:text-lg pr-8 break-words">
              Attendees {showAttendees && `\u00b7 ${showAttendees.title}`}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Directory of everyone registered for this event. Search by name or organisation, or toggle your own visibility.
            </DialogDescription>
          </DialogHeader>

          {/* Signed-out prompt — matches how other member directories behave */}
          {!isLoggedIn ? (
            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-6 text-center" data-testid="attendees-signin-prompt">
              <Users className="h-8 w-8 mx-auto text-slate-400 mb-2" />
              <p className="text-sm text-slate-600 mb-4">Sign in to see who's coming and message other attendees.</p>
              <Button onClick={() => navigate('/login')} className="bg-brand-navy hover:bg-brand-dark text-white" data-testid="attendees-signin-btn">
                <LogIn className="h-4 w-4 mr-2" /> Sign In
              </Button>
            </div>
          ) : (
            <>
              {/* Header row — visibility chip (member) or Show hidden (admin) */}
              {(showAttendees?.my_registration || (user?.role === 'admin' && showAttendees?.hidden_count > 0)) && (
                <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                  {showAttendees?.my_registration ? (
                    <button
                      type="button"
                      onClick={() => handleToggleVisibility(showAttendees.event_id, !showAttendees.my_registration.visibility_hidden)}
                      data-testid="directory-visibility-chip"
                      className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                        showAttendees.my_registration.visibility_hidden
                          ? 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
                          : 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                      }`}
                    >
                      {showAttendees.my_registration.visibility_hidden ? (
                        <><EyeOff className="h-3.5 w-3.5" /> You're hidden</>
                      ) : (
                        <><Eye className="h-3.5 w-3.5" /> You're visible</>
                      )}
                    </button>
                  ) : <span />}

                  {user?.role === 'admin' && showAttendees?.hidden_count > 0 && (
                    <button
                      type="button"
                      onClick={() => setAdminShowHidden(v => !v)}
                      data-testid="directory-show-hidden-toggle"
                      className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                        adminShowHidden
                          ? 'bg-brand-navy text-white border-brand-navy'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}
                      title={adminShowHidden ? 'Hide the private bookings again' : 'Reveal attendees who have hidden themselves'}
                    >
                      <Lock className="h-3.5 w-3.5" /> {adminShowHidden ? 'Showing hidden' : `Show hidden (${showAttendees.hidden_count})`}
                    </button>
                  )}
                </div>
              )}

              {/* Filter bar — search only in phase 1; sits inside a container
                  that role/sector/interests filters can slot into later
                  without shuffling the layout. */}
              <div className="mt-3 mb-2 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2" data-testid="directory-filter-bar">
                <Search className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <input
                  type="text"
                  value={directorySearch}
                  onChange={e => setDirectorySearch(e.target.value)}
                  placeholder="Search by name or organisation"
                  className="flex-1 bg-transparent text-sm placeholder:text-slate-400 focus:outline-none"
                  data-testid="directory-search-input"
                  aria-label="Search attendees"
                />
                {directorySearch && (
                  <button
                    type="button"
                    onClick={() => setDirectorySearch('')}
                    className="text-xs text-slate-400 hover:text-slate-600"
                    data-testid="directory-search-clear"
                  >Clear</button>
                )}
              </div>

              <div className="space-y-1 mt-2">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-1">All attendees</p>
                {(() => {
                  const isAdmin = user?.role === 'admin';
                  const q = normaliseForSearch(directorySearch);
                  // Base list = every attendee row from backend
                  const base = showAttendees?.attendees || [];
                  const rows = base.filter(a => {
                    // Admin visibility toggle — hide hidden peers unless
                    // toggled on. Members never see hidden peers (backend
                    // already stripped them), but the caller's own row
                    // is included when they're self-hidden.
                    if (isAdmin && a.visibility_hidden && !adminShowHidden && a.user_id !== user?.user_id) return false;
                    if (!q) return true;
                    const hay = normaliseForSearch(`${a.name || ''} ${a.organisation || ''}`);
                    return hay.includes(q);
                  });
                  if (rows.length === 0) {
                    // Empty state: either nothing registered yet, or everything
                    // filtered out. The RSVP button on the event card stays
                    // reachable — this modal doesn't block registration.
                    const nothingRegistered = base.length === 0;
                    return (
                      <div className="py-8 text-center" data-testid="directory-empty">
                        <Users className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                        <p className="text-sm text-slate-500">
                          {q
                            ? `No matches for "${directorySearch.trim()}".`
                            : nothingRegistered
                              ? "No one's put themselves forward yet — you could be first."
                              : "No attendees visible right now."}
                        </p>
                      </div>
                    );
                  }
                  return rows.map(a => {
                    const isMatch = meetRecs.some(r => r.user_id === a.user_id);
                    const isSelf = a.user_id === user?.user_id;
                    const hiddenBadge = a.visibility_hidden && !isSelf;
                    return (
                      <button
                        key={a.user_id}
                        type="button"
                        onClick={() => setViewingMember(a)}
                        className={`w-full flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors text-left ${hiddenBadge ? 'opacity-60' : ''}`}
                        data-testid={`attendee-row-${a.user_id}`}
                      >
                        <AttendeeAvatar attendee={a} size="md" />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-slate-700 block truncate flex items-center gap-1.5">
                            {a.name}
                            {hiddenBadge && (
                              <Lock className="h-3 w-3 text-slate-400" aria-label="Hidden from peers" />
                            )}
                            {isSelf && (
                              <span className="text-[10px] font-semibold text-slate-400">(you)</span>
                            )}
                          </span>
                          {(a.role || a.organisation) ? (
                            <span className="text-xs text-slate-500 block truncate" data-testid={`attendee-role-org-${a.user_id}`}>
                              {[a.role, a.organisation].filter(Boolean).join(' · ')}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">View profile</span>
                          )}
                        </div>
                        {hiddenBadge && (
                          <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200 flex-shrink-0" data-testid={`attendee-hidden-badge-${a.user_id}`}>
                            Hidden from peers
                          </span>
                        )}
                        {isMatch && !hiddenBadge && (
                          <span className="text-[10px] font-semibold text-brand-sage bg-brand-sage/10 px-2 py-0.5 rounded-full border border-brand-sage/30 flex-shrink-0">
                            Match
                          </span>
                        )}
                        {!isSelf && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); navigate(`/messages?to=${a.user_id}&event_id=${showAttendees.event_id}`); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); navigate(`/messages?to=${a.user_id}&event_id=${showAttendees.event_id}`); } }}
                            className="text-slate-400 hover:text-brand-navy transition-colors flex-shrink-0 cursor-pointer"
                            title="Message about this event"
                            data-testid={`attendee-message-${a.user_id}`}
                          >
                            <MessageSquare className="h-4 w-4" />
                          </span>
                        )}
                        <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
                      </button>
                    );
                  });
                })()}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* In-place Member Profile Modal */}
      <MemberProfileModal
        member={viewingMember}
        open={!!viewingMember}
        onClose={() => setViewingMember(null)}
        user={user}
        eventContext={showAttendees?.event_id ? { event_id: showAttendees.event_id, event_title: showAttendees.title, date: showAttendees.date, meeting_durations: showAttendees.meeting_durations, meeting_points: showAttendees.meeting_points } : null}
        onMeetingRequested={(mr) => { if (mr?.event_id) loadMeetingsForEvent(mr.event_id); }}
      />

      {/* Networking-availability editor — opened from the Set availability
          link on any registered event card. */}
      <AvailabilityEditor
        open={!!showAvailabilityFor}
        onClose={() => setShowAvailabilityFor(null)}
        event={showAvailabilityFor}
        onSaved={() => { if (showAvailabilityFor?.event_id) loadMeetingsForEvent(showAvailabilityFor.event_id); }}
      />

      {/* My Event bottom-sheet — mobile-first consolidated view with
          next-up tile, quick actions, pending meetings and chronological
          timeline (sessions + accepted meetings). Reuses the top-level
          Attendees modal, event-filtered Messages route, Availability
          editor and Member Profile modal as the handlers for its three
          quick actions and meeting-row profile/message buttons. */}
      <MyEventSheet
        open={!!showMyEventFor}
        onClose={() => setShowMyEventFor(null)}
        event={showMyEventFor}
        user={user}
        onOpenAttendees={() => setShowAttendees(showMyEventFor)}
        onOpenMessages={() => navigate(`/messages?event_id=${showMyEventFor?.event_id}`)}
        onOpenAvailability={() => setShowAvailabilityFor(showMyEventFor)}
        onOpenProfile={(attendee) => setViewingMember(attendee)}
        onMeetingAction={handleMeetingAction}
      />
    </div>
  );
}
